/**
 * Derived from svg-path-properties@0.2.2 and svgpath/a2c vendored by flubber@0.4.2 (MIT).
 * Adapted for dependency-free internal ESM use in anvil2.
 */

const TAU = Math.PI * 2;

type ArcCenter = [number, number, number, number];
type ArcCurve = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

function unitVectorAngle(ux: number, uy: number, vx: number, vy: number) {
  const sign = ux * vy - uy * vx < 0 ? -1 : 1;
  let dot = ux * vx + uy * vy;

  if (dot > 1.0) {
    dot = 1.0;
  }

  if (dot < -1.0) {
    dot = -1.0;
  }

  return sign * Math.acos(dot);
}

function getArcCenter(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  fa: number,
  fs: number,
  rx: number,
  ry: number,
  sinPhi: number,
  cosPhi: number,
): ArcCenter {
  const x1p = (cosPhi * (x1 - x2)) / 2 + (sinPhi * (y1 - y2)) / 2;
  const y1p = (-sinPhi * (x1 - x2)) / 2 + (cosPhi * (y1 - y2)) / 2;

  const rxSq = rx * rx;
  const rySq = ry * ry;
  const x1pSq = x1p * x1p;
  const y1pSq = y1p * y1p;

  let radicant = rxSq * rySq - rxSq * y1pSq - rySq * x1pSq;

  if (radicant < 0) {
    radicant = 0;
  }

  radicant /= rxSq * y1pSq + rySq * x1pSq;
  radicant = Math.sqrt(radicant) * (fa === fs ? -1 : 1);

  const cxp = (radicant * rx * y1p) / ry;
  const cyp = (-radicant * ry * x1p) / rx;

  const cx = cosPhi * cxp - sinPhi * cyp + (x1 + x2) / 2;
  const cy = sinPhi * cxp + cosPhi * cyp + (y1 + y2) / 2;

  const v1x = (x1p - cxp) / rx;
  const v1y = (y1p - cyp) / ry;
  const v2x = (-x1p - cxp) / rx;
  const v2y = (-y1p - cyp) / ry;

  const theta1 = unitVectorAngle(1, 0, v1x, v1y);
  let deltaTheta = unitVectorAngle(v1x, v1y, v2x, v2y);

  if (fs === 0 && deltaTheta > 0) {
    deltaTheta -= TAU;
  }

  if (fs === 1 && deltaTheta < 0) {
    deltaTheta += TAU;
  }

  return [cx, cy, theta1, deltaTheta];
}

function approximateUnitArc(theta1: number, deltaTheta: number): ArcCurve {
  const alpha = (4 / 3) * Math.tan(deltaTheta / 4);

  const x1 = Math.cos(theta1);
  const y1 = Math.sin(theta1);
  const x2 = Math.cos(theta1 + deltaTheta);
  const y2 = Math.sin(theta1 + deltaTheta);

  return [
    x1,
    y1,
    x1 - y1 * alpha,
    y1 + x1 * alpha,
    x2 + y2 * alpha,
    y2 - x2 * alpha,
    x2,
    y2,
  ];
}

export default function a2c(
  x1: number,
  y1: number,
  rx: number,
  ry: number,
  phi: number,
  fa: number,
  fs: number,
  x2: number,
  y2: number,
): ArcCurve[] {
  const sinPhi = Math.sin((phi * TAU) / 360);
  const cosPhi = Math.cos((phi * TAU) / 360);

  const x1p = (cosPhi * (x1 - x2)) / 2 + (sinPhi * (y1 - y2)) / 2;
  const y1p = (-sinPhi * (x1 - x2)) / 2 + (cosPhi * (y1 - y2)) / 2;

  if (x1p === 0 && y1p === 0) {
    return [];
  }

  if (rx === 0 || ry === 0) {
    return [];
  }

  rx = Math.abs(rx);
  ry = Math.abs(ry);

  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);

  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }

  const [cx, cy, initialTheta, initialDeltaTheta] = getArcCenter(
    x1,
    y1,
    x2,
    y2,
    fa,
    fs,
    rx,
    ry,
    sinPhi,
    cosPhi,
  );
  const result: ArcCurve[] = [];
  let theta1 = initialTheta;
  let deltaTheta = initialDeltaTheta;

  const segments = Math.max(Math.ceil(Math.abs(deltaTheta) / (TAU / 4)), 1);
  deltaTheta /= segments;

  for (let index = 0; index < segments; index += 1) {
    result.push(approximateUnitArc(theta1, deltaTheta));
    theta1 += deltaTheta;
  }

  return result.map((curve) => {
    for (let index = 0; index < curve.length; index += 2) {
      let x = curve[index];
      let y = curve[index + 1];

      x *= rx;
      y *= ry;

      const xp = cosPhi * x - sinPhi * y;
      const yp = sinPhi * x + cosPhi * y;

      curve[index] = xp + cx;
      curve[index + 1] = yp + cy;
    }

    return curve;
  });
}
